import ListActionsMenu from "../documents/ListActionsMenu";
import SifenReportModal from "../sifen-reports/SifenReportModal";
import {
  CButton,
  CButtonGroup,
  CDropdown,
  CDropdownItem,
  CDropdownToggle,
  CTooltip,
} from "@coreui/react";
import {
  REMISSION_NOTE_STATUS_APPROVED_BY_SET,
  REMISSION_NOTE_STATUS_CANCELLATION_FAILED,
  REMISSION_NOTE_STATUS_CANCELLATION_REQUESTED,
  REMISSION_NOTE_STATUS_ERROR,
  REMISSION_NOTE_STATUS_PENDING,
  REMISSION_NOTE_STATUS_RECOVERABLE_ERROR,
  REMISSION_NOTE_STATUS_RESEND_REQUESTED,
  REMISSION_NOTE_STATUS_SET_UNKNOWN_ERROR,
  REMISSION_NOTE_STATUS_UPLOADED_TO_SET,
  RemissionNote,
} from "../../models/remission-note";
import React, { useEffect, useState } from "react";
import { useSub } from "../../hooks/pub-sub";
import {
  REMISSION_NOTES_DESTROY,
  REMISSION_NOTES_RETRIEVE,
  REMISSION_NOTES_UPDATE,
} from "../../auth/permissions";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";

const MAX_VISIBLE_ACTIONS = 3;

interface RemissionNoteCrudActionsProps {
  remissionNote: RemissionNote;
  onCancellationClick: (_: RemissionNote) => void;
  onCancellationRetryClick: (_: RemissionNote) => void;
  onCancellationRevertClick: (_: RemissionNote) => void;
  onUpdateClick: (_: RemissionNote) => void;
  onDeleteClick: (_: RemissionNote) => void;
  onConsultByCdcClick: (_: RemissionNote) => void;
  onResendClick: (_: RemissionNote) => void;
  onSendEmailClick: (_: RemissionNote) => void;
  onHistoryClick: (_: RemissionNote) => void;
}

const RemissionNoteCrudActions: React.FC<RemissionNoteCrudActionsProps> = ({
  remissionNote,
  onCancellationClick,
  onCancellationRetryClick,
  onCancellationRevertClick,
  onUpdateClick,
  onDeleteClick,
  onConsultByCdcClick,
  onResendClick,
  onSendEmailClick,
  onHistoryClick,
}) => {
  const [showSifenReport, setShowSifenReport] = useState(false);
  const [currentRemissionNote, setCurrentRemissionNote] =
    useState(remissionNote);

  useEffect(() => {
    setCurrentRemissionNote(remissionNote);
  }, [remissionNote]);

  useSub(
    `remission_note_${remissionNote.id}`,
    (remissionNote: RemissionNote) => {
      setCurrentRemissionNote(remissionNote);
    },
  );

  const cancelButton = (
    <PermissionRequiredComponent permissionName={REMISSION_NOTES_UPDATE}>
      <CTooltip content="Anular">
        <CButton
          className="text-white"
          color="secondary"
          onClick={() => {
            onCancellationClick(remissionNote);
          }}
        >
          <i className="fa fa-ban"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const editButton = (
    <PermissionRequiredComponent permissionName={REMISSION_NOTES_UPDATE}>
      <CTooltip content="Editar">
        <CButton
          className="text-white"
          color="warning"
          onClick={() => {
            onUpdateClick(remissionNote);
          }}
        >
          <i className="fa fa-pencil"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const deleteButton = (
    <PermissionRequiredComponent permissionName={REMISSION_NOTES_DESTROY}>
      <CTooltip content="Eliminar">
        <CButton
          className="text-white"
          color="danger"
          onClick={() => {
            onDeleteClick(remissionNote);
          }}
        >
          <i className="fa fa-trash"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const documentDateTime = new Date(currentRemissionNote.createdAt!).getTime();
  const now = new Date().getTime();

  const olderThan45Days = documentDateTime < now - 45 * 24 * 60 * 60 * 1000;

  const shouldShowCancelButton =
    currentRemissionNote.status === REMISSION_NOTE_STATUS_APPROVED_BY_SET &&
    !olderThan45Days;
  const shouldShowEditAndDeleteButton =
    (currentRemissionNote.status === REMISSION_NOTE_STATUS_PENDING ||
      currentRemissionNote.status === REMISSION_NOTE_STATUS_ERROR ||
      currentRemissionNote.status === REMISSION_NOTE_STATUS_SET_UNKNOWN_ERROR ||
      currentRemissionNote.setResponseCode === "1001" ||
      currentRemissionNote.setResponseCode === "4003") &&
    currentRemissionNote.status !== REMISSION_NOTE_STATUS_RECOVERABLE_ERROR;

  const consultByCdcButton = (
    <PermissionRequiredComponent permissionName={REMISSION_NOTES_UPDATE}>
      <CTooltip content="Consultar por CDC">
        <CButton
          className="text-white"
          color="primary"
          onClick={() => {
            onConsultByCdcClick(remissionNote);
          }}
        >
          <i className="fa fa-question-circle"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowConsultByCdcButton =
    currentRemissionNote.status === REMISSION_NOTE_STATUS_UPLOADED_TO_SET ||
    currentRemissionNote.status === REMISSION_NOTE_STATUS_ERROR ||
    currentRemissionNote.status === REMISSION_NOTE_STATUS_RESEND_REQUESTED ||
    currentRemissionNote.status ===
      REMISSION_NOTE_STATUS_CANCELLATION_REQUESTED ||
    currentRemissionNote.setResponseCode === "1001" ||
    currentRemissionNote.setResponseCode === "4003";

  const resendButton = (
    <PermissionRequiredComponent permissionName={REMISSION_NOTES_UPDATE}>
      <CTooltip content="Reenviar">
        <CButton
          className="text-white"
          color="success"
          onClick={() => {
            onResendClick(remissionNote);
          }}
        >
          <i className="fa fa-refresh"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowResendButton =
    currentRemissionNote.status === REMISSION_NOTE_STATUS_ERROR;

  const sendEmailButton = (
    <PermissionRequiredComponent permissionName={REMISSION_NOTES_UPDATE}>
      <CTooltip
        content={
          currentRemissionNote.emailSent ? "Reenviar Correo" : "Enviar Correo"
        }
      >
        <CButton
          className="text-white"
          color="info"
          onClick={() => {
            onSendEmailClick(remissionNote);
          }}
        >
          <i className="fa fa-envelope"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowSendEmailButton =
    currentRemissionNote.status === REMISSION_NOTE_STATUS_APPROVED_BY_SET;

  const retryCancellationButton = (
    <PermissionRequiredComponent permissionName={REMISSION_NOTES_UPDATE}>
      <CTooltip content="Reintentar Anulación">
        <CButton
          className="text-white"
          color="warning"
          onClick={() => {
            onCancellationRetryClick(currentRemissionNote);
          }}
        >
          <i className="fa fa-rotate-right"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowRetryCancellationButton =
    currentRemissionNote.status === REMISSION_NOTE_STATUS_CANCELLATION_FAILED;

  const revertCancellationButton = (
    <PermissionRequiredComponent permissionName={REMISSION_NOTES_UPDATE}>
      <CTooltip content="Revertir Anulación">
        <CButton
          className="text-white"
          color="success"
          onClick={() => {
            onCancellationRevertClick(currentRemissionNote);
          }}
        >
          <i className="fa fa-backward"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowRevertCancellationButton =
    currentRemissionNote.status === REMISSION_NOTE_STATUS_CANCELLATION_FAILED;

  const historyButton = (
    <PermissionRequiredComponent permissionName={REMISSION_NOTES_RETRIEVE}>
      <CTooltip content="Ver Historial">
        <CButton
          className="text-white"
          color="dark"
          onClick={() => {
            onHistoryClick(currentRemissionNote);
          }}
        >
          <i className="fa fa-history"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const allActions = [
    {
      element: historyButton,
      shouldShow: true,
      label: "Ver Historial",
      icon: "fa-history",
      permission: REMISSION_NOTES_RETRIEVE,
      onClick: () => onHistoryClick(currentRemissionNote),
    },
    {
      element: cancelButton,
      shouldShow: shouldShowCancelButton,
      label: "Anular",
      icon: "fa-ban",
      permission: REMISSION_NOTES_UPDATE,
      onClick: () => onCancellationClick(remissionNote),
    },
    {
      element: editButton,
      shouldShow: shouldShowEditAndDeleteButton,
      label: "Editar",
      icon: "fa-pencil",
      permission: REMISSION_NOTES_UPDATE,
      onClick: () => onUpdateClick(remissionNote),
    },
    {
      element: deleteButton,
      shouldShow: shouldShowEditAndDeleteButton,
      label: "Eliminar",
      icon: "fa-trash",
      permission: REMISSION_NOTES_DESTROY,
      onClick: () => onDeleteClick(remissionNote),
    },
    {
      element: consultByCdcButton,
      shouldShow: shouldShowConsultByCdcButton,
      label: "Consultar por CDC",
      icon: "fa-question-circle",
      permission: REMISSION_NOTES_UPDATE,
      onClick: () => onConsultByCdcClick(remissionNote),
    },
    {
      element: resendButton,
      shouldShow: shouldShowResendButton,
      label: "Reenviar",
      icon: "fa-refresh",
      permission: REMISSION_NOTES_UPDATE,
      onClick: () => onResendClick(remissionNote),
    },
    {
      element: retryCancellationButton,
      shouldShow: shouldShowRetryCancellationButton,
      label: "Reintentar Anulación",
      icon: "fa-rotate-right",
      permission: REMISSION_NOTES_UPDATE,
      onClick: () => onCancellationRetryClick(currentRemissionNote),
    },
    {
      element: revertCancellationButton,
      shouldShow: shouldShowRevertCancellationButton,
      label: "Revertir Anulación",
      icon: "fa-backward",
      permission: REMISSION_NOTES_UPDATE,
      onClick: () => onCancellationRevertClick(currentRemissionNote),
    },
    {
      element: sendEmailButton,
      shouldShow: shouldShowSendEmailButton,
      label: currentRemissionNote.emailSent
        ? "Reenviar Correo"
        : "Enviar Correo",
      icon: "fa-envelope",
      permission: REMISSION_NOTES_UPDATE,
      onClick: () => onSendEmailClick(remissionNote),
    },
    {
      element: (
        <PermissionRequiredComponent permissionName={REMISSION_NOTES_UPDATE}>
          <CTooltip content="Preparar reporte para DNIT">
            <CButton color="warning" aria-label="Preparar reporte para DNIT" onClick={() => setShowSifenReport(true)}>
              <i className="fa fa-flag"></i>
            </CButton>
          </CTooltip>
        </PermissionRequiredComponent>
      ),
      shouldShow: currentRemissionNote.status === "cancelled",
      label: "Preparar reporte para DNIT",
      icon: "fa-flag",
      permission: REMISSION_NOTES_UPDATE,
      onClick: () => setShowSifenReport(true),
    },
  ];

  const visibleActions = allActions.filter((a) => a.shouldShow);
  const shouldCollapse = visibleActions.length > MAX_VISIBLE_ACTIONS;
  const primaryActions = shouldCollapse
    ? visibleActions.slice(0, MAX_VISIBLE_ACTIONS - 1)
    : visibleActions;
  const overflowActions = shouldCollapse
    ? visibleActions.slice(MAX_VISIBLE_ACTIONS - 1)
    : [];

  return (
    <td className="text-center">
      <CButtonGroup>
        {primaryActions.map((a, i) => (
          <React.Fragment key={i}>{a.element}</React.Fragment>
        ))}
        {shouldCollapse && (
          <CDropdown className="btn-group">
            <CTooltip content="Más opciones">
              <CDropdownToggle
                color="primary"
                className="text-white"
                caret={false}
                style={{ borderTopRightRadius: "0.25rem", borderBottomRightRadius: "0.25rem" }}
              >
                <i className="fa fa-ellipsis-v"></i>
              </CDropdownToggle>
            </CTooltip>
            <ListActionsMenu>
              {overflowActions.map((a, i) => (
                <PermissionRequiredComponent
                  key={i}
                  permissionName={a.permission}
                >
                  <CDropdownItem onClick={a.onClick}>
                    <i className={`fa ${a.icon} mr-2`}></i>
                    {a.label}
                  </CDropdownItem>
                </PermissionRequiredComponent>
              ))}
            </ListActionsMenu>
          </CDropdown>
        )}
      </CButtonGroup>
      {showSifenReport && <SifenReportModal
        show={showSifenReport}
        documentType="electronic_remission_note"
        documentId={currentRemissionNote.id}
        onClose={() => setShowSifenReport(false)}
      />}
    </td>
  );
};

export default RemissionNoteCrudActions;
